import React, { useState } from 'react';
import { SchemaProperty, ChannelFormData } from '../types';
import { UseResponseSchemaReturn } from '../hooks/useResponseSchema';

export interface OutboundTabProps {
  // Form Data
  formData: ChannelFormData;
  
  secrets: Array<{ id: string; name: string; secret_id: string; description?: string }>;
  openSecretsDropdown: number | null;
  setOpenSecretsDropdown: (index: number | null) => void;
  
  // Handlers
  handleInputChange: (field: keyof ChannelFormData, value: string | boolean) => void;
  setIsModified: (modified: boolean) => void;
  setFormData: React.Dispatch<React.SetStateAction<ChannelFormData>>;
  handleSchemaChange: (value: string, isResponse: boolean, isMetadata: boolean) => void;
  
  // Response schema hook (contains all response schema-related state and functions)
  responseSchema: UseResponseSchemaReturn;
}

const OutboundTab: React.FC<OutboundTabProps> = ({
  formData,
  secrets,
  openSecretsDropdown,
  setOpenSecretsDropdown,
  handleInputChange,
  setIsModified,
  setFormData,
  handleSchemaChange,
  responseSchema,
}) => {
  const [outboundInfoCollapsed, setOutboundInfoCollapsed] = useState(true);

  return (
    <>
      {/* <p className="text-muted mb-3">
        Validation rules and processing of the data prior to the Gateway returning the result to the calling agent.
      </p> */}

      {/* How it works - Outbound */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light" style={{ cursor: 'pointer' }} onClick={() => setOutboundInfoCollapsed(!outboundInfoCollapsed)}>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-muted">
              <i className="fas fa-graduation-cap"></i> <i>How Outbound Identity and Metadata Works</i>
            </h6>
            <i className={`fas fa-chevron-${outboundInfoCollapsed ? 'down' : 'up'}`}></i>
          </div>
        </div>
        {!outboundInfoCollapsed && (
        <div className="card-body">
          
          <div className="alert alert-info mb-3">
              <i className="fas fa-info-circle"></i>
              <strong className='text-nowrap'>Key Insight:</strong> The "outbound" features control how the Gateway processes and enhances responses flowing back from target agents to original callers. This is the opposite direction from inbound (requests). You can both validate backend agent identities AND inject custom metadata into responses.
          </div>

          <div className="card bg-light border-0">
            <div className="card-body">
              <h6 className="font-weight-bold text-dark mb-3">
                <i className="fas fa-arrow-left mr-2"></i>
                Two Separate Outbound Capabilities
              </h6>

              <h6 className="font-weight-bold text-primary mb-2 mt-3">
                1. Response Extension Rules (Identity Validation)
              </h6>
              <p className="mb-3 text-gray-800">
                When a backend agent responds to a request, it may include its own identity information (LLM provider, software version, capabilities) in the response metadata. 
                The Gateway can validate this response structure and extract the backend agent's identity for tracking and auditing purposes.
              </p>

              <p className="mb-3 text-gray-800">
                <strong>Purpose:</strong> Know which specific backend agents are handling requests. Track LLM providers, software versions, and capabilities. Build an identity map of all backend agents your Gateway communicates with.
              </p>

              <p className="mb-3 text-gray-800">
                <strong>How it works:</strong>
              </p>
              <ol className="mb-3 text-gray-800">
                <li className="mb-2">Backend agent includes the agent-identity extension in its response</li>
                <li className="mb-2">Gateway validates the response structure against your configured schema</li>
                <li className="mb-2">Gateway extracts the agent's identity (DID) from the validated data</li>
                <li className="mb-2">Identity is recorded in metrics for auditing: "which agent handled this request?"</li>
              </ol>

              <div className="border rounded p-3 mb-3" style={{backgroundColor: '#f8f9fa'}}>
                <strong>Example Response from Backend Agent:</strong>
                <pre className="mb-0 mt-2" style={{fontSize: '0.85rem', color: '#495057'}}>
{`{
  "result": {
    "history": [{
      "extensions": [
        "https://fabric.affinidi.io/extensions/agent-identity/v1"
      ],
      "metadata": {
        "https://fabric.affinidi.io/extensions/agent-identity/v1": {
          "agentIdentity": {
            "llmInfo": {
              "provider": "Anthropic",
              "model": "claude-3-sonnet"
            },
            "softwareInfo": {
              "name": "Backend Service",
              "version": "2.0"
            }
          }
        }
      }
    }]
  }
}`}
                </pre>
              </div>

              <p className="mb-3 text-gray-800">
                The Gateway validates this structure, extracts the backend agent's DID/identity, and records it in metrics for tracking which agents are serving requests.
              </p>

              <h6 className="font-weight-bold text-primary mb-2 mt-4">
                2. Response Custom Metadata (Data Injection)
              </h6>
              <p className="mb-3 text-gray-800">
                Inject additional metadata into responses flowing back through the Gateway, allowing you to add gateway-specific information, environment tags, or custom tracking data.
              </p>

              <p className="mb-3 text-gray-800">
                <strong>Purpose:</strong> Add Gateway version info, region tags, processing timestamps, or custom telemetry to all responses. This metadata helps clients understand how their request was processed.
              </p>

              <p className="mb-3 text-gray-800">
                <strong>How it works:</strong>
              </p>
              <ol className="mb-3 text-gray-800">
                <li className="mb-2">Gateway receives response from backend agent</li>
                <li className="mb-2"><strong>Before</strong> sending it back to the original caller</li>
                <li className="mb-2">Gateway injects your configured metadata into the response</li>
                <li className="mb-2">Modified response is sent to the client</li>
              </ol>

              <div className="border rounded p-3 mb-3" style={{backgroundColor: '#f8f9fa'}}>
                <strong>Example Configuration:</strong>
                <pre className="mb-0 mt-2" style={{fontSize: '0.85rem', color: '#495057'}}>
{`{
  "response_custom_metadata": {
    "enabled": true,
    "payload": {
      "gateway_version": "1.0.0",
      "region": "us-west-2",
      "processed_by": "trust-gateway-prod"
    }
  }
}`}
                </pre>
              </div>

              <div className="border rounded p-3 mb-3" style={{backgroundColor: '#f8f9fa'}}>
                <strong>Response After Injection:</strong>
                <pre className="mb-0 mt-2" style={{fontSize: '0.85rem', color: '#495057'}}>
{`{
  "result": { ... },
  "_meta": {
    "gateway_version": "1.0.0",
    "region": "us-west-2",
    "processed_by": "trust-gateway-prod"
  }
}`}
                </pre>
              </div>

              <h6 className="font-weight-bold text-dark mb-3 mt-4">
                <i className="fas fa-exchange-alt mr-2"></i>
                Complete Request/Response Flow
              </h6>
              
              <div className="border rounded p-3 mb-3" style={{backgroundColor: '#fff3cd'}}>
                <pre className="mb-0" style={{fontSize: '0.85rem', fontFamily: 'monospace', color: '#856404', whiteSpace: 'pre-wrap'}}>
{`Client Request
    ↓
Gateway (Inbound Processing)
    ↓ [validate inbound extensions]
    ↓ [inject request custom_metadata]
    ↓
Request sent to Backend Agent →
                                ↓
                    Backend Agent processes
                                ↓
                        ← Response
    ↓
Gateway (Outbound Processing)
    ↓ [validate response_extension_rules]
    ↓ [extract backend agent identity → save to DB]
    ↓ [inject response_custom_metadata]
    ↓
Modified Response sent to Client`}
                </pre>
              </div>

              <h6 className="font-weight-bold text-dark mb-3 mt-4">
                <i className="fas fa-balance-scale mr-2"></i>
                Inbound vs Outbound
              </h6>
              
              <div className="table-responsive mb-3">
                <table className="table table-sm table-bordered mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th style={{width: '20%'}}>Direction</th>
                      <th style={{width: '40%'}}>Identity Purpose</th>
                      <th style={{width: '40%'}}>Metadata Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Inbound</strong></td>
                      <td>Identify the <strong>calling client</strong> making the request</td>
                      <td>Add custom data to <strong>requests</strong> before forwarding</td>
                    </tr>
                    <tr>
                      <td><strong>Outbound</strong></td>
                      <td>Identify the <strong>backend agent</strong> handling the request</td>
                      <td>Add custom data to <strong>responses</strong> before returning</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h6 className="font-weight-bold text-dark mb-3 mt-4">
                <i className="fas fa-lightbulb mr-2"></i>
                Use Cases for Outbound Features
              </h6>
              
              <ul className="mb-3 text-gray-800">
                <li className="mb-2"><strong>Backend Agent Tracking:</strong> Know which LLM provider/version handled each request</li>
                <li className="mb-2"><strong>Compliance & Auditing:</strong> Verify responses come from authorized agents with specific capabilities</li>
                <li className="mb-2"><strong>Performance Metrics:</strong> Group analytics by backend agent identity to identify bottlenecks</li>
                <li className="mb-2"><strong>Gateway Telemetry:</strong> Add processing time, Gateway version, region info to responses</li>
                <li className="mb-2"><strong>Client Debugging:</strong> Help clients understand the complete processing pipeline</li>
                <li className="mb-2"><strong>Multi-Region Tracking:</strong> Identify which datacenter/region processed the request</li>
              </ul>

              <div className="alert alert-warning mb-0">
                <i className="fas fa-exclamation-triangle"></i>
                <strong>Important:</strong> Response extension rules and response custom metadata are independent features. You can use one, both, or neither depending on your needs. Identity validation helps you track backend agents, while metadata injection helps you add context to responses.
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Identity Section */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-fingerprint"></i> Identity Creation
            </h6>
            <div className="d-flex align-items-center gap-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="response-identity-enabled"
                  checked={formData.enforce_response_extension}
                  onChange={(e) => {
                    handleInputChange('enforce_response_extension', e.target.checked);
                    setIsModified(true);
                  }}
                />
                <label className="form-check-label" htmlFor="response-identity-enabled" style={{ color: formData.enforce_response_extension ? '#000' : '#6c757d' }}>
                  Enabled
                </label>
              </div>
            </div>
          </div>
        </div>
        {formData.enforce_response_extension && (
        <div className="card-body">
          {/* Identity Meta Field Configuration */}
          <div className="mb-4">
            <label htmlFor="channel-edit-mcp-response-meta-field" className="form-label fw-bold">
              Identity Meta Field Name
            </label>
            <input
              type="text"
              className="form-control"
              id="channel-edit-mcp-response-meta-field"
              value={formData.mcp_identity_config?.meta_field || 'agentIdentity'}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  mcp_identity_config: {
                    ...formData.mcp_identity_config,
                    enabled: formData.mcp_identity_config?.enabled || false,
                    meta_field: e.target.value
                  }
                });
                setIsModified(true);
              }}
              placeholder="agentIdentity"
            />
            <small className="form-text text-muted">
              The field name within <code>_meta</code> that contains the agent identity object in responses (e.g., <code>_meta.agentIdentity</code>)
            </small>
          </div>
    
    {/* Response Visual Schema Editor */}
    <div className="mb-3">
      <label className="form-label fw-bold">Response JSON Schema Properties</label>
      <div className="mb-2">
        <button
          type="button"
          style={{ marginRight: "10px" }}
          className="btn btn-sm btn-primary me-3"
          onClick={() => {
            responseSchema.setResponseSchemaProperties(responseSchema.responseSchemaProperties.map(p => ({ ...p, collapsed: false })));
          }}
        >
          Expand All
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary ms-3"
          onClick={() => {
            responseSchema.setResponseSchemaProperties(responseSchema.responseSchemaProperties.map(p => ({ ...p, collapsed: true })));
          }}
        >
          Collapse All
        </button>
      </div>
      <div id="response-schema-properties-list" className="mb-2">
        {responseSchema.responseSchemaProperties.map((property, index) => {
          // Check if this property should be visible (not hidden by collapsed parent)
          if (property.level > 0 && !responseSchema.isResponseChildVisible(property, property.parent)) {
            return null;
          }
          
          return (
          <div key={`response-prop-${index}`} className="schema-property-card" data-level={property.level}>
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-2">
                {property.type === 'object' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-info"
                    onClick={() => responseSchema.toggleResponsePropertyCollapsed(index)}
                    title={property.collapsed ? "Expand children" : "Collapse children"}
                  >
                    {property.collapsed ? '↓' : '↑'}
                  </button>
                )}
                <input
                  type="text"
                  className="form-control"
                  placeholder="Property name"
                  value={property.name}
                  data-property-index={index.toString()}
                  data-field-type="response-name"
                  onChange={(e) => responseSchema.updateResponsePropertyName(index, e.target.value)}
                  onBlur={() => responseSchema.updateResponseSchemaJson()}
                />
                <select
                  className="form-control property-type-select"
                  value={property.type}
                  data-property-index={index.toString()}
                  data-field-type="response-type"
                  onChange={(e) => responseSchema.updateResponsePropertyType(index, e.target.value)}
                  onBlur={() => responseSchema.updateResponseSchemaJson()}
                  title="Property type"
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="object">Object</option>
                  <option value="array">Array</option>
                </select>
                {property.type === 'object' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={() => responseSchema.addResponseChildProperty(index)}
                    title="Add child property"
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => responseSchema.removeResponseProperty(index)}
                  title="Remove property"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
              
              <div className="d-flex align-items-center gap-5">
                {['string', 'number', 'boolean'].includes(property.type) && (
                  <>
                    <div className="form-check" title="Include in identity hash computation" style={{ marginRight: '10px' }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`response-identity-${index}`}
                        checked={property.xIdentity}
                        onChange={(e) => responseSchema.updateResponsePropertyXIdentity(index, e.target.checked)}
                        onBlur={() => responseSchema.updateResponseSchemaJson()}
                      />
                      <label className="form-check-label" htmlFor={`response-identity-${index}`}>
                        Identity
                      </label>
                    </div>
                    <div className="form-check" title={property.xIdentity ? "Identity fields are automatically required" : "Property is required"}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`response-required-${index}`}
                        checked={property.required}
                        disabled={property.xIdentity}
                        onChange={(e) => responseSchema.updateResponsePropertyRequired(index, e.target.checked)}
                        onBlur={() => responseSchema.updateResponseSchemaJson()}
                      />
                      <label className="form-check-label" htmlFor={`response-required-${index}`}>
                        Required
                      </label>
                    </div>
                  </>
                )}
              </div>
              
              {/* Validation Rules */}
              {(['string', 'number'].includes(property.type) || property.type === 'array') && (
                <div className="mt-2">
                  <div className="row">
                    <div className="col-md-4">
                      <select
                        className="form-control form-control-sm"
                        value={property.validationRule?.type || ''}
                        data-property-index={index.toString()}
                        data-field-type="response-validation-type"
                        onChange={(e) => responseSchema.updateResponseValidationRuleType(index, e.target.value)}
                        onBlur={() => responseSchema.updateResponseSchemaJson()}
                        title="Validation rule type"
                      >
                        <option value="">No validation</option>
                        {property.type !== 'array' && (
                          <>
                            <option value="equals">Equals</option>
                            <option value="contains">Contains</option>
                            {property.type !== 'number' && <option value="regex">Regex</option>}
                            {property.type === 'number' && <option value="range">Range</option>}
                          </>
                        )}
                        {property.type === 'array' && (
                          <>
                            <option value="array-all">Array All Elements</option>
                            <option value="array-any">Array Any Element</option>
                            <option value="array-length">Array Length</option>
                          </>
                        )}
                      </select>
                    </div>
                    {property.validationRule && (
                      <div className="col-md-8">
                        {!property.validationRule.type.startsWith('array-') ? (
                          <>
                            <input
                              type="text"
                              className={`form-control form-control-sm ${
                                property.validationRule.type === 'regex' && property.validationRule.value 
                                  ? ((() => {
                                      try {
                                        new RegExp(property.validationRule.value);
                                        return 'is-valid';
                                      } catch (e) {
                                        return 'is-invalid';
                                      }
                                    })()
                                  ) : ''
                              }`}
                              placeholder={
                                property.validationRule.type === 'equals' ? 'Enter expected value...' :
                                property.validationRule.type === 'contains' ? 'Enter text that must be contained...' :
                                property.validationRule.type === 'regex' ? 'Enter regex pattern (e.g., ^[A-Z]+$)...' :
                                property.validationRule.type === 'range' ? 'Enter range as min-max (e.g., 1-100 or -100 or 1-)...' :
                                'Validation value'
                              }
                              value={property.validationRule.value || ''}
                              data-property-index={index.toString()}
                              data-field-type="response-validation-value"
                              onChange={(e) => responseSchema.updateResponseValidationRuleValue(index, e.target.value)}
                              onBlur={() => responseSchema.updateResponseSchemaJson()}
                            />
                            <div className="form-text text-muted" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                              {property.validationRule.type === 'equals' && 'Field must exactly match this value'}
                              {property.validationRule.type === 'contains' && 'Field must contain this text'}
                              {property.validationRule.type === 'regex' && (
                                property.validationRule.value ? (
                                  (() => {
                                    try {
                                      new RegExp(property.validationRule.value);
                                      return <span className="text-success"><i className="fas fa-check"></i> Valid regex pattern</span>;
                                    } catch (e) {
                                      return <span className="text-danger"><i className="fas fa-times"></i> Invalid regex pattern</span>;
                                    }
                                  })()
                                ) : 'Enter a JavaScript-compatible regular expression'
                              )}
                              {property.validationRule.type === 'range' && 'Format: min-max (leave min or max empty for unbounded, e.g., "-100" for max 100)'}
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Array validation inputs */}
                            {(property.validationRule.type === 'array-all' || property.validationRule.type === 'array-any') && (
                              <div className="mb-2">
                                <label className="form-label form-label-sm">Element Type</label>
                                <select
                                  className="form-control form-control-sm"
                                  value={property.validationRule.elementType || 'string'}
                                  onChange={(e) => responseSchema.updateResponseValidationRuleElementType(index, e.target.value)}
                                  title="Array element type"
                                >
                                  <option value="string">String</option>
                                  <option value="number">Number</option>
                                  <option value="boolean">Boolean</option>
                                  <option value="object">Object</option>
                                  <option value="array">Array</option>
                                </select>
                              </div>
                            )}
                            {(property.validationRule.type === 'array-all' || property.validationRule.type === 'array-any') && (
                              <div className="mb-2">
                                <label className="form-label form-label-sm">Allowed Values (optional)</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Comma-separated values (e.g., value1, value2, value3)"
                                  value={property.validationRule.values || ''}
                                  onChange={(e) => responseSchema.updateResponseValidationRuleValues(index, e.target.value)}
                                  onBlur={() => responseSchema.updateResponseSchemaJson()}
                                />
                              </div>
                            )}
                            {property.validationRule.type === 'array-length' && (
                              <div className="row">
                                <div className="col-6">
                                  <label className="form-label form-label-sm">Min Length</label>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    placeholder="Min"
                                    min="0"
                                    value={property.validationRule.min || ''}
                                    onChange={(e) => responseSchema.updateResponseValidationRuleMin(index, e.target.value)}
                                    onBlur={() => responseSchema.updateResponseSchemaJson()}
                                  />
                                </div>
                                <div className="col-6">
                                  <label className="form-label form-label-sm">Max Length</label>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    placeholder="Max"
                                    min="0"
                                    value={property.validationRule.max || ''}
                                    onChange={(e) => responseSchema.updateResponseValidationRuleMax(index, e.target.value)}
                                    onBlur={() => responseSchema.updateResponseSchemaJson()}
                                  />
                                </div>
                              </div>
                            )}
                            <div className="form-text text-muted" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                              {property.validationRule.type === 'array-all' && 'All array elements must match the specified type and values'}
                              {property.validationRule.type === 'array-any' && 'At least one array element must match the specified type and values'}
                              {property.validationRule.type === 'array-length' && 'Array must have length within the specified range'}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>
      <button
        type="button"
        className="btn btn-sm btn-success mt-2"
        onClick={responseSchema.addResponseSchemaProperty}
      >
        <i className="fas fa-plus"></i> Add Root Property
      </button>
    </div>

    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label fw-bold mb-0 d-flex align-items-center gap-2">
          Response Schema Raw JSON
          {!responseSchema.responseSchemaJsonValid && (
            <span className="badge badge-danger ml-2">
              <i className="fas fa-exclamation-triangle"></i> Invalid JSON
            </span>
          )}
          {responseSchema.responseSchemaJsonValid && responseSchema.responseSchemaJson.trim() && (
            <span className="badge badge-success ml-2">
              <i className="fas fa-check"></i> Valid JSON
            </span>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={responseSchema.syncResponseVisualWithJson}
            disabled={!responseSchema.responseSchemaJsonValid}
            title="Sync visual editor with JSON changes"
          >
            <i className="fas fa-sync"></i> Sync Visual
          </button>
        </label>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => responseSchema.setShowResponseJson(!responseSchema.showResponseJson)}
          title={responseSchema.showResponseJson ? "Hide JSON" : "Show JSON"}
        >
          <i className={`fas fa-${responseSchema.showResponseJson ? 'eye-slash' : 'eye'}`}></i> {responseSchema.showResponseJson ? 'Hide' : 'Show'} JSON
        </button>
      </div>
      {responseSchema.showResponseJson && (
      <div className={`schema-property-card ${!responseSchema.responseSchemaJsonValid ? 'border-danger' : responseSchema.responseSchemaJsonValid && responseSchema.responseSchemaJson.trim() ? 'border-success' : ''}`}>
        <textarea 
          className={`form-control schema-raw-json ${!responseSchema.responseSchemaJsonValid ? 'is-invalid' : responseSchema.responseSchemaJsonValid && responseSchema.responseSchemaJson.trim() ? 'is-valid' : ''}`}
          id="response-schema-raw-json"
          rows={15}
          value={responseSchema.responseSchemaJson}
          onChange={(e) => handleSchemaChange(e.target.value, true, false)}
          onInput={(e) => handleSchemaChange((e.target as HTMLTextAreaElement).value, true, false)}
          onKeyUp={(e) => handleSchemaChange((e.target as HTMLTextAreaElement).value, true, false)}
          placeholder="Edit JSON schema directly..."
        />
        {!responseSchema.responseSchemaJsonValid && (
          <div className="invalid-feedback d-block">
            <i className="fas fa-exclamation-triangle"></i> 
            {responseSchema.responseSchemaValidationErrors.length > 0 ? (
              <div>
                {responseSchema.responseSchemaValidationErrors.map((error, idx) => (
                  <div key={idx}>{error}</div>
                ))}
              </div>
            ) : (
              'Invalid JSON'
            )}
          </div>
        )}
      </div>
      )}
    </div>
    </div>
    )}
    </div>
    
    {/* Injection Section */}
    <div className="card shadow-sm mb-4">
      <div className="card-header bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-primary">
            <i className="fas fa-syringe"></i> Custom Metadata Injection
          </h6>
          <div className="d-flex align-items-center gap-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="response-injection-enabled"
                checked={formData.response_custom_metadata?.enabled || false}
                onChange={(e) => {
                  setIsModified(true);
                  setFormData(prev => ({
                    ...prev,
                    response_custom_metadata: {
                      ...prev.response_custom_metadata,
                      enabled: e.target.checked
                    }
                  }));
                }}
              />
              <label className="form-check-label" htmlFor="response-injection-enabled" style={{ color: (formData.response_custom_metadata?.enabled || false) ? '#000' : '#6c757d' }}>
                Enabled
              </label>
            </div>
          </div>
        </div>
      </div>
      {(formData.response_custom_metadata?.enabled || false) && (
      <div className="card-body">
        <p className="text-muted mb-3">
          Configure custom metadata for response injection. This metadata is automatically injected by the Gateway on outgoing responses.
        </p>
        
        {formData.response_custom_metadata?.enabled && (
          <>
            {/* MCP Injection Target Selection */}
            {(formData.protocol === 'mcp' || formData.protocol === 'mpx') && (
              <div className="mb-4 p-3 bg-light border rounded">
                <label className="form-label fw-bold mb-2">
                  <i className="fas fa-bullseye text-primary me-2"></i> Metadata Injection Target</label>
                <p className="text-muted small mb-3">
                  Choose where to inject metadata for MCP responses. <code>_meta</code> field is protocol-native and transport-agnostic. 
                  HTTP headers are transport-level only. Use <strong>both</strong> for maximum compatibility.
                </p>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="injection_target_response"
                      id="injection-meta-response"
                      value="meta"
                      checked={formData.response_custom_metadata?.injection_target === 'meta'}
                      onChange={(e) => {
                        setIsModified(true);
                        setFormData(prev => ({
                          ...prev,
                          response_custom_metadata: {
                            ...prev.response_custom_metadata,
                            injection_target: 'meta'
                          }
                        }));
                      }}
                    />
                    <label className="form-check-label" htmlFor="injection-meta-response">
                      <code>_meta</code> field only
                      <small className="d-block text-muted">JSON-RPC protocol-level</small>
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="injection_target_response"
                      id="injection-headers-response"
                      value="headers"
                      checked={formData.response_custom_metadata?.injection_target === 'headers'}
                      onChange={(e) => {
                        setIsModified(true);
                        setFormData(prev => ({
                          ...prev,
                          response_custom_metadata: {
                            ...prev.response_custom_metadata,
                            injection_target: 'headers'
                          }
                        }));
                      }}
                    />
                    <label className="form-check-label" htmlFor="injection-headers-response">
                      HTTP headers only
                      <small className="d-block text-muted">Transport-level (HTTP only)</small>
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="injection_target_response"
                      id="injection-both-response"
                      value="both"
                      checked={!formData.response_custom_metadata?.injection_target || formData.response_custom_metadata?.injection_target === 'both'}
                      onChange={(e) => {
                        setIsModified(true);
                        setFormData(prev => ({
                          ...prev,
                          response_custom_metadata: {
                            ...prev.response_custom_metadata,
                            injection_target: 'both'
                          }
                        }));
                      }}
                    />
                    <label className="form-check-label" htmlFor="injection-both-response">
                      <strong>Both</strong> (recommended)
                      <small className="d-block text-muted">Maximum compatibility</small>
                    </label>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mb-3">
              <label className="form-label fw-bold">Channel Metadata Properties (Visual Editor)</label>
              <p className="text-muted small">Define key-value pairs for your channel metadata. Values can be nested objects, arrays, strings, numbers, or booleans.</p>
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={responseSchema.addResponseMetadataProperty}
                >
                  <i className="fas fa-plus"></i> Add Property
                </button>
              </div>
              <div id="metadata-properties-list-response" className="mb-2">
                {responseSchema.responseMetadataSchemaProperties.map((property, index) => {
                  // Check if this property should be visible (not hidden by collapsed parent)
                  if (property.level > 0 && !responseSchema.isMetadataChildVisible(property, property.parent)) {
                    return null;
                  }
                  
                  return (
                    <div key={`metadata-prop-${index}`} className="schema-property-card mb-2" data-level={property.level}>
                      <div className="card-body p-2">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          {property.type === 'object' && (
                            <button
                              type="button"
                              className="btn btn-sm btn-info"
                              onClick={() => responseSchema.toggleResponseMetadataPropertyCollapsed(index)}
                              title={property.collapsed ? "Expand children" : "Collapse children"}
                            >
                              {property.collapsed ? '↓' : '↑'}
                            </button>
                          )}
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Property name"
                            value={property.name}
                            style={{ maxWidth: '200px' }}
                            onChange={(e) => {
                              const newProps = [...responseSchema.responseMetadataSchemaProperties];
                              newProps[index].name = e.target.value;
                              responseSchema.setResponseMetadataSchemaProperties(newProps);
                              setIsModified(true);
                            }}
                            onBlur={() => responseSchema.updateResponseMetadataJsonFromProperties()}
                          />
                          {property.type !== 'object' && (
                            <div className="d-flex gap-2 flex-grow-1">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder='Value (e.g., "text", 123, true, [1,2,3]) or $SECRET:secret_name'
                                value={(property as any).value || ''}
                                onChange={(e) => {
                                  const newProps = [...responseSchema.responseMetadataSchemaProperties];
                                  const { type } = responseSchema.parseValueType(e.target.value);
                                  (newProps[index] as any).value = e.target.value;
                                  newProps[index].type = type;
                                  responseSchema.setResponseMetadataSchemaProperties(newProps);
                                  setIsModified(true);
                                }}
                                onBlur={() => responseSchema.updateResponseMetadataJsonFromProperties()}
                              />
                              <div className="dropdown" style={{ position: 'relative' }}>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => setOpenSecretsDropdown(openSecretsDropdown === index ? null : index)}
                                  title="Select secret"
                                >
                                  <i className="fas fa-key"></i>
                                </button>
                                {openSecretsDropdown === index && (
                                <ul className="dropdown-menu dropdown-menu-end show" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 1000 }}>
                                  {secrets.length === 0 ? (
                                    <li><span className="dropdown-item-text text-muted">No secrets available</span></li>
                                  ) : (
                                    secrets.map((secret) => (
                                      <li key={secret.id}>
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() => {
                                            const newProps = [...responseSchema.responseMetadataSchemaProperties];
                                            (newProps[index] as any).value = `$SECRET:${secret.secret_id}`;
                                            newProps[index].type = 'string';
                                            responseSchema.setResponseMetadataSchemaProperties(newProps);
                                            setIsModified(true);
                                            responseSchema.updateResponseMetadataJsonFromProperties();
                                            setOpenSecretsDropdown(null);
                                          }}
                                        >
                                          <div>
                                            <strong>{secret.name}</strong>
                                            <div className="text-muted small">ID: {secret.secret_id}</div>
                                            {secret.description && (
                                              <div className="text-muted small">{secret.description}</div>
                                            )}
                                          </div>
                                        </button>
                                      </li>
                                    ))
                                  )}
                                </ul>
                                )}
                              </div>
                            </div>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => responseSchema.addResponseMetadataChildProperty(index)}
                            title="Add nested property"
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => responseSchema.removeResponseMetadataProperty(index)}
                            title="Remove property"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                        {property.type !== 'object' && (property as any).value && (
                          <div className="text-muted small">
                            Type: <span className="badge badge-info">{property.type}</span>
                            {((property as any).value as string)?.startsWith('$SECRET:') && (
                              <span className="badge badge-warning ml-2">
                                <i className="fas fa-key"></i> Secret
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label fw-bold mb-0 d-flex align-items-center gap-2">
                  <span>
                    Extension Metadata JSON (Direct Editor)
                    {!responseSchema.responseMetadataJsonValid && (
                      <span className="text-danger ml-2">
                        <i className="fas fa-exclamation-triangle"></i> Invalid JSON
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => {
                    // Sync visual editor from JSON
                    try {
                      const parsed = JSON.parse(responseSchema.responseMetadataSchemaJson);
                      const props: SchemaProperty[] = [];
                      
                      const parseObject = (obj: any, level: number, parentIndex?: number) => {
                        Object.keys(obj).forEach(key => {
                          const val = obj[key];
                          let type = 'string';
                          let valueStr = '';
                          
                          if (typeof val === 'number') {
                            type = 'number';
                            valueStr = String(val);
                          } else if (typeof val === 'boolean') {
                            type = 'boolean';
                            valueStr = String(val);
                          } else if (Array.isArray(val)) {
                            type = 'array';
                            valueStr = JSON.stringify(val);
                          } else if (typeof val === 'object' && val !== null) {
                            type = 'object';
                          } else {
                            type = 'string';
                            valueStr = String(val);
                          }
                          
                          const propIndex = props.length;
                          props.push({
                            name: key,
                            type,
                            required: false,
                            xIdentity: false,
                            level,
                            parent: parentIndex,
                            collapsed: false,
                            value: valueStr
                          });
                          
                          // If it's an object, recursively parse children
                          if (type === 'object') {
                            parseObject(val, level + 1, propIndex);
                          }
                        });
                      };
                      
                      parseObject(parsed, 0);
                      responseSchema.setResponseMetadataSchemaProperties(props);
                    } catch (e) {
                      console.error('Failed to parse metadata JSON:', e);
                    }
                  }}
                    disabled={!responseSchema.responseMetadataJsonValid}
                    title="Sync visual editor with JSON changes"
                  >
                    <i className="fas fa-sync"></i> Sync Visual
                  </button>
                </label>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => responseSchema.setShowResponseMetadataJson(!responseSchema.showResponseMetadataJson)}
                  title={responseSchema.showResponseMetadataJson ? "Hide JSON" : "Show JSON"}
                >
                  <i className={`fas fa-${responseSchema.showResponseMetadataJson ? 'eye-slash' : 'eye'}`}></i> {responseSchema.showResponseMetadataJson ? 'Hide' : 'Show'} JSON
                </button>
              </div>
              {responseSchema.showResponseMetadataJson && (
              <div className={`schema-property-card ${!responseSchema.responseMetadataJsonValid ? 'border-danger' : responseSchema.responseMetadataJsonValid && responseSchema.responseMetadataSchemaJson.trim() && responseSchema.responseMetadataSchemaJson.trim() !== '{}' ? 'border-success' : ''}`}>
                <textarea 
                  className={`form-control schema-raw-json ${!responseSchema.responseMetadataJsonValid ? 'is-invalid' : responseSchema.responseMetadataJsonValid && responseSchema.responseMetadataSchemaJson.trim() && responseSchema.responseMetadataSchemaJson.trim() !== '{}' ? 'is-valid' : ''}`}
                  id="metadata-schema-raw-json-response"
                  rows={15}
                  value={responseSchema.responseMetadataSchemaJson}
                  onChange={(e) => {
                    handleSchemaChange(e.target.value, true, true);
                  }}
                  placeholder='Enter response metadata as JSON (e.g., {"environment": "production", "responseSource": "gateway"})'
                />
                {!responseSchema.responseMetadataJsonValid && (
                  <div className="invalid-feedback d-block">
                    <i className="fas fa-exclamation-triangle"></i> Invalid JSON
                  </div>
                )}
              </div>
              )}
              <small className="form-text text-muted">
                Enter custom JSON metadata to inject into responses. This metadata is added on the way OUT from the gateway.
              </small>
            </div>
          </>
        )}
      </div>
      )}
    </div>
    </>
  );
};

export default OutboundTab;
